import { useState, useMemo, useCallback } from "react";
import type { BookingItemData } from "../../../mocks/bookingData";
import type { CartItem } from "../page";

interface ItemsFilterListProps {
  items: BookingItemData[];
  cartItems: CartItem[];
  onQtyChange: (item: BookingItemData, delta: number) => void;
}

export default function ItemsFilterList({ items, cartItems, onQtyChange }: ItemsFilterListProps) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(items.map((i) => i.category)))],
    [items]
  );

  const filtered = useMemo(() => {
    return items.filter((item) => {
      const matchCat = activeCategory === "All" || item.category === activeCategory;
      const matchSearch = !search || item.label.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [items, search, activeCategory]);

  const showGrouped = activeCategory === "All" && !search;

  const grouped = useMemo(() => {
    if (!showGrouped) return {} as Record<string, BookingItemData[]>;
    return filtered.reduce((acc, item) => {
      if (!acc[item.category]) acc[item.category] = [];
      acc[item.category].push(item);
      return acc;
    }, {} as Record<string, BookingItemData[]>);
  }, [filtered, showGrouped]);

  const getQty = useCallback((id: string) => cartItems.find((c) => c.id === id)?.quantity ?? 0, [cartItems]);

  const ItemRow = ({ item }: { item: BookingItemData }) => {
    const qty = getQty(item.id);
    const active = qty > 0;
    return (
      <div className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0 transition-colors ${active ? "bg-[#f6fff0]" : "bg-white"}`}>
        {/* Icon */}
        <span className={`w-10 h-10 flex items-center justify-center rounded-xl border-2 text-lg flex-shrink-0 ${active ? "bg-cl-green border-[#4a8c0a] text-white" : "bg-cl-lime border-cl-green text-cl-green"}`}>
          <i className={item.icon}></i>
        </span>

        {/* Label + price — tapping this adds 1 */}
        <button
          onPointerUp={(e) => { e.preventDefault(); if (qty === 0) onQtyChange(item, 1); }}
          style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          className="flex-1 text-left min-w-0"
        >
          <p className={`text-sm font-black leading-tight ${active ? "text-cl-green" : "text-cl-dark"}`}>{item.label}</p>
          {(item as BookingItemData & { note?: string }).note && (
            <p className="text-[10px] text-gray-400 font-semibold leading-snug">{(item as BookingItemData & { note?: string }).note}</p>
          )}
          <p className="text-xs text-gray-500 font-semibold">£{item.unitPrice.toFixed(2)} inc. VAT</p>
        </button>

        {/* +/- stepper — always on right, large tap targets */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onPointerUp={(e) => { e.preventDefault(); onQtyChange(item, -1); }}
            disabled={qty === 0}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            className={`w-11 h-11 flex items-center justify-center rounded-xl border-2 font-black text-xl transition-colors select-none ${qty === 0 ? "border-gray-100 text-gray-200" : "border-cl-dark text-cl-dark active:bg-cl-dark active:text-white"}`}
          >
            <i className="ri-subtract-line"></i>
          </button>
          <span className={`w-8 text-center font-black text-base tabular-nums ${active ? "text-cl-green" : "text-gray-400"}`}>{qty}</span>
          <button
            onPointerUp={(e) => { e.preventDefault(); onQtyChange(item, 1); }}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            className="w-11 h-11 flex items-center justify-center rounded-xl border-2 border-cl-dark text-cl-dark active:bg-cl-dark active:text-white font-black text-xl transition-colors select-none"
          >
            <i className="ri-add-line"></i>
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Search */}
      <div className="relative mb-3">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm z-10"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search items e.g. sofa, fridge, mattress…"
          className="w-full border-2 border-transparent rounded-xl pl-10 pr-10 py-3.5 text-sm font-semibold text-cl-dark placeholder-gray-400 focus:outline-none focus:border-cl-dark transition-colors"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cl-dark cursor-pointer w-8 h-8 flex items-center justify-center"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 flex-wrap mb-4 overflow-x-auto pb-1">
        {categories.map((cat) => (
          <button
            key={cat}
            onPointerUp={(e) => { e.preventDefault(); setActiveCategory(cat); }}
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            className={`text-xs font-black px-4 py-2.5 rounded-full border-2 transition-all whitespace-nowrap select-none ${
              activeCategory === cat
                ? "bg-cl-dark text-white border-cl-dark"
                : "bg-white text-cl-dark border-gray-200"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Item list — row layout for reliable mobile tapping */}
      {showGrouped ? (
        <div className="flex flex-col gap-4">
          {Object.entries(grouped).map(([category, catItems]) => (
            <div key={category} className="bg-white rounded-2xl cartoon-card overflow-hidden">
              <div className="px-5 py-3 bg-cl-tint border-b border-gray-100">
                <p className="text-xs font-black text-cl-dark uppercase tracking-widest">{category}</p>
              </div>
              {catItems.map((item) => <ItemRow key={item.id} item={item} />)}
            </div>
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="bg-white rounded-2xl cartoon-card overflow-hidden">
          {activeCategory !== "All" && (
            <div className="px-5 py-3 bg-cl-tint border-b border-gray-100">
              <p className="text-xs font-black text-cl-dark uppercase tracking-widest">{activeCategory}</p>
            </div>
          )}
          {filtered.map((item) => <ItemRow key={item.id} item={item} />)}
        </div>
      ) : (
        <div className="bg-white rounded-2xl cartoon-card px-5 py-14 text-center">
          <i className="ri-search-line text-4xl text-gray-200 mb-3 block"></i>
          <p className="text-sm text-gray-400 font-semibold">No items found for &ldquo;{search}&rdquo;</p>
          <button onClick={() => setSearch("")} className="mt-3 text-xs text-cl-green font-black cursor-pointer hover:underline">
            Clear search
          </button>
        </div>
      )}
    </div>
  );
}
